// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { TranslateService } from "@ngx-translate/core";
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class BalancesService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private translateService: TranslateService
  ) { }

  /**
   * Create a new balance
   *
   * @since 1.0.0
   *
   * @param balance Balance data
   * @returns Observable<any>
   */
  create(balance: any): Observable<any> {
    return this.apiService.apiCall("POST", "balances", balance);
  }

  /**
   * Get balances list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "balances", params);
  }

  /**
   * Get balances list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getPropertyList(
    propertyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      search = {
        ...search,
        propertyId,
      };
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "balances", params);
  }

  /**
   * Get balances list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getCompanyList(
    companyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      search = {
        ...search,
        companyId,
      };
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "balances", params);
  }

  /**
   * Get an exportation file of all balances
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService
      .apiCall("GET", "balances/export", {}, { responseType: "blob" })
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all closed balances
   *
   * @since 1.0.0
   */
  getExportClosed(): Observable<any> {
    return this.apiService
      .apiCall("GET", "balances/export-closed", {}, { responseType: "blob" })
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all open first balances
   *
   * @since 1.0.0
   */
  getExportOpenFirst(): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        "balances/export-open-first",
        {},
        { responseType: "blob" }
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all open following balances
   *
   * @since 1.0.0
   */
  getExportOpenFollowing(): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        "balances/export-open-following",
        {},
        { responseType: "blob" }
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all balances
   *
   * @since 1.0.0
   */
  getExportCompany(companyId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `balances/export-company/${companyId}`,
        {},
        { responseType: "blob" }
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all balances
   *
   * @since 1.0.0
   */
  getExportProperty(propertyId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `balances/export-property/${propertyId}`,
        {},
        { responseType: "blob" }
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get given balanceId data from back-end
   *
   * @since 1.0.0
   *
   * @param balanceId ID of the balance to get
   */
  get(balanceId: string): Observable<any> {
    return this.apiService.apiCall("GET", `balances/${balanceId}`).pipe(
      catchError((err) => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update balance with given balanceId with given balance data
   *
   * @since 1.0.0
   *
   * @param balanceId ID of balance to update
   * @param data New balance's data
   */
  update(balanceId: string, data: object): Observable<any> {
    return this.apiService.apiCall("PUT", `balances/${balanceId}`, data);
  }

  /**
   * Update balance status
   *
   * @since 1.0.0
   *
   * @param balanceId ID of balance to update
   * @param status New balance's data
   */
  changeStatus(balanceId: string, data: any): Observable<any> {
    return this.apiService.apiCall("PATCH", `balances/${balanceId}`, data);
  }

  /**
   * Delete permanently a balance
   *
   * @since 1.0.0
   *
   * @param balanceId ID of the balance
   */
  delete(balanceId: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `balances/${balanceId}`);
  }

  /**
   * Delete permanently a note balance
   *
   * @since 1.0.0
   *
   * @param balanceId ID of the balance
   * @param noteId ID of the note
   */
  deleteNote(balanceId: string, noteId: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `balances/${balanceId}/notes/${noteId}`);
  }
  
  /**
   * Update balance status
   *
   * @since 1.0.0
   *
   * @param balanceId ID of balance to update
   * @param noteId ID of the note to update (sent in the body, not in the URL)
   * @param data New text of note
   */
   updateNote(balanceId: string, noteId: string, data: any): Observable<any> {
    return this.apiService.apiCall("PUT", `balances/${balanceId}/notes`, { noteId, text: data.text });
  }
}
